import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { from, throwError, timeout, catchError, switchMap } from 'rxjs';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';
import { AvailabilityService } from './availability.service';

/**
 * HTTP interceptor (functional, Angular 20 style).
 *
 * - Attaches Firebase token when available
 * - Always calls own API even without token (world feed, single post are public)
 * - 5s token timeout: if token retrieval hangs, continue without Authorization
 * - 15s request timeout
 * - Only applies to own API URLs, not Firebase/Google/third-party
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const availability = inject(AvailabilityService);
  const apiBase = environment.apiBaseUrl;

  // Only intercept own API calls
  if (!req.url.startsWith(apiBase)) {
    return next(req);
  }

  const mode = availability.mode();
  if (mode === 'OFFLINE') {
    return throwError(() => new HttpErrorResponse({
      error: { detail: availability.message() },
      status: 503,
      statusText: 'Service Unavailable',
      url: req.url
    }));
  }

  if (mode === 'READ_ONLY' && isWriteMethod(req.method)) {
    return throwError(() => new HttpErrorResponse({
      error: { detail: availability.message() },
      status: 503,
      statusText: 'Read Only',
      url: req.url
    }));
  }

  return from(authService.getToken()).pipe(
    switchMap(token => {
      let cloned = req;
      if (token) {
        cloned = req.clone({
          setHeaders: { Authorization: `Bearer ${token}` }
        });
      }
      return next(cloned).pipe(
        timeout(30000),
        catchError(err => {
          console.log(err, ', network error ', JSON.stringify(err));
          if (err.name === 'TimeoutError') {
            return throwError(() => new HttpErrorResponse({
              error: { detail: 'Request timed out. Please try again.' },
              status: 0, statusText: 'Timeout'
            }));
          }
          if (err instanceof HttpErrorResponse && err.status === 404) {
            // console.log( ' authInter err ', err)
            const detail: string = err.error?.detail ?? '';
            if (detail.includes('call POST /api/users/me') && !authService.isRegistrationInProgress) {
              // console.log( ' authInter logging out ')
              authService.logout();
            }
          }
          return throwError(() => err);
        })
      );
    })
  );
};

function isWriteMethod(method: string): boolean {
  return method === 'POST'
    || method === 'PUT'
    || method === 'PATCH'
    || method === 'DELETE';
}
